import { supabase } from '../supabase';

const baseUrl = import.meta.env.VITE_API_BASE_URL;

if (!baseUrl) {
  throw new Error('VITE_API_BASE_URL es obligatoria. Revisa .env.');
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }

  const response = await fetch(`${baseUrl}${path}`, { ...init, headers });

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body,
      `API ${response.status} en ${init?.method ?? 'GET'} ${path}`,
    );
  }

  return body as T;
}

/**
 * Descarga un archivo binario (ej. XLSX) desde el backend con auth y dispara
 * la descarga en el navegador. El nombre sale del header Content-Disposition
 * si está presente; si no, usa `nombreFallback`.
 */
export async function descargarArchivo(
  path: string,
  nombreFallback: string,
): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers = new Headers();
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }

  const response = await fetch(`${baseUrl}${path}`, { headers });
  if (!response.ok) {
    throw new ApiError(
      response.status,
      null,
      `API ${response.status} en GET ${path}`,
    );
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const nombre = match ? match[1] : nombreFallback;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
